import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { WizardSteps } from '../components/push-campaigns/WizardSteps';
import { AudienceStep } from '../components/push-campaigns/AudienceStep';
import { ContentStep } from '../components/push-campaigns/ContentStep';
import { ScheduleStep } from '../components/push-campaigns/ScheduleStep';
import { TestSendPanel } from '../components/push-campaigns/TestSendPanel';
import { ConfirmSendDialog } from '../components/push-campaigns/ConfirmSendDialog';
import { useAuth } from '../context/AuthContext';
import { canLaunchPushCampaigns, canTestSendPushCampaigns } from '../utils/pushCampaignPermissions';
import {
  EMPTY_PUSH_AUDIENCE_FILTERS,
  formatTestEmails,
  getPushCampaignFailureMessage,
  parseTestEmails,
  readPushAudienceFilters,
  toPushAudienceJson,
  validatePushAudienceFilters,
  validatePushCampaignContent,
  validateTestEmails,
} from '../utils/pushCampaignValidation';
import {
  createPushCampaign,
  getPushCampaign,
  isPushCampaignConflict,
  updatePushCampaign,
  type PushAudienceFilters,
  type PushAudienceType,
  type PushCampaign,
  type PushCampaignEstimate,
  type PushCampaignPayload,
  type PushSendMode,
} from '../services/resortAdminApi';

const LAST_STEP = 3;

interface PushCampaignForm {
  campaign_name: string;
  audience_type: PushAudienceType;
  filters: PushAudienceFilters;
  test_emails: string;
  title_en: string;
  body_en: string;
  title_fr: string;
  body_fr: string;
  picture_link: string;
  send_mode: PushSendMode;
  scheduled_at: string;
}

const EMPTY_FORM: PushCampaignForm = {
  campaign_name: '',
  audience_type: 'test_list',
  filters: EMPTY_PUSH_AUDIENCE_FILTERS,
  test_emails: '',
  title_en: '',
  body_en: '',
  title_fr: '',
  body_fr: '',
  picture_link: '',
  send_mode: 'now',
  scheduled_at: '',
};

export const PushCampaignWizard: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const campaignName = params.name ?? null;
  const step = clampStep(searchParams.get('step'));

  const [form, setForm] = useState<PushCampaignForm>(EMPTY_FORM);
  const [campaign, setCampaign] = useState<PushCampaign | null>(null);
  const [estimate, setEstimate] = useState<PushCampaignEstimate | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const canLaunch = canLaunchPushCampaigns(auth.user?.role ?? null);
  const canTestSend = canTestSendPushCampaigns(auth.user?.role ?? null);
  const isEditable = campaign == null || campaign.status === 'Draft';
  const isContentTested =
    campaign != null &&
    campaign.content_hash != null &&
    campaign.content_hash !== '' &&
    campaign.content_hash === campaign.test_sent_content_hash;

  const applyCampaign = useCallback((value: PushCampaign) => {
    const nextForm = toForm(value);
    setCampaign(value);
    setForm(nextForm);
    setIsDirty(false);
  }, []);

  const reload = useCallback(async () => {
    if (campaignName == null) {
      return;
    }
    setIsLoading(true);
    setSaveError(null);
    try {
      applyCampaign(await getPushCampaign(campaignName));
    } catch (failure) {
      setSaveError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'Failed to load the campaign.'));
    } finally {
      setIsLoading(false);
    }
  }, [campaignName, applyCampaign]);

  useEffect(() => { reload(); }, [reload]);

  const handleTestSent = useCallback(async () => {
    if (campaignName == null) {
      return;
    }
    try {
      setCampaign(await getPushCampaign(campaignName));
    } catch (failure) {
      setSaveError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'Failed to reload the campaign.'));
    }
  }, [campaignName]);

  useEffect(() => {
    setErrors([]);
  }, [step]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handler = (x: BeforeUnloadEvent) => {
      x.preventDefault();
      x.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [isDirty]);

  const updateForm = useCallback((patch: Partial<PushCampaignForm>) => {
    setForm(x => ({ ...x, ...patch }));
    setIsDirty(true);
  }, []);

  const goToStep = (value: number) => {
    setSearchParams({ step: String(value) }, { replace: true });
  };

  const validateStep = (value: number): string[] => {
    if (value === 1) {
      const stepErrors: string[] = [];
      if (form.campaign_name.trim() === '') {
        stepErrors.push('Campaign name is required.');
      }
      if (form.audience_type === 'test_list') {
        const emailError = validateTestEmails(form.test_emails);
        if (emailError != null) {
          stepErrors.push(emailError);
        }
      }
      return stepErrors.concat(validatePushAudienceFilters(form.audience_type, form.filters));
    }
    if (value === 2) {
      return validatePushCampaignContent(form);
    }
    if (value === 3 && form.send_mode === 'scheduled' && form.scheduled_at === '') {
      return ['Pick the date and time to start sending.'];
    }
    return [];
  };

  const save = async (): Promise<PushCampaign | null> => {
    const payload = toPayload(form);
    setIsSaving(true);
    setSaveError(null);
    try {
      if (campaign == null) {
        const created = await createPushCampaign(payload);
        applyCampaign(created);
        return created;
      }
      if (!isDirty) {
        return campaign;
      }
      const updated = await updatePushCampaign({ ...payload, name: campaign.name, modified: campaign.modified });
      applyCampaign(updated);
      return updated;
    } catch (failure) {
      if (isPushCampaignConflict(failure)) {
        setIsConflictOpen(true);
        return null;
      }
      setSaveError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'Failed to save the campaign.'));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    const stepErrors = validateStep(step);
    setErrors(stepErrors);
    if (stepErrors.length > 0) {
      return;
    }
    const saved = await save();
    if (saved == null) {
      return;
    }
    const nextStep = Math.min(LAST_STEP, step + 1);
    if (campaignName == null) {
      navigate(`/push-campaigns/${encodeURIComponent(saved.name)}/edit?step=${nextStep}`, { replace: true });
      return;
    }
    goToStep(nextStep);
  };

  const handleSaveDraft = async () => {
    const stepErrors = validateStep(step);
    setErrors(stepErrors);
    if (stepErrors.length > 0) {
      return;
    }
    const saved = await save();
    if (saved != null) {
      navigate('/push-campaigns');
    }
  };

  const handleLaunched = (value: PushCampaign) => {
    setIsConfirmOpen(false);
    setIsDirty(false);
    navigate(`/push-campaigns/${encodeURIComponent(value.name)}`);
  };

  const launchBlockReason = (): string | null => {
    if (campaign == null) {
      return 'Save the draft before sending.';
    }
    if (isDirty) {
      return 'Save the current step before sending.';
    }
    if (!isContentTested) {
      return 'Send a test of the current content before sending.';
    }
    if (estimate != null && estimate.reachable === 0) {
      return 'The audience is empty, there is nobody to send to.';
    }
    if (form.send_mode === 'scheduled' && form.scheduled_at === '') {
      return 'Pick the date and time to start sending.';
    }
    return null;
  };

  if (isLoading && campaign == null) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (campaignName != null && campaign == null) {
    return (
      <div className="space-y-4">
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{saveError ?? 'The campaign could not be loaded.'}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => reload()}
            className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark"
          >
            Reload
          </button>
          <button
            onClick={() => navigate('/push-campaigns')}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to campaigns
          </button>
        </div>
      </div>
    );
  }

  if (!isEditable) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-800">{form.campaign_name}</h1>
        <p className="text-sm text-gray-600">
          The campaign is no longer a draft and cannot be edited.
        </p>
        <button
          onClick={() => navigate(`/push-campaigns/${encodeURIComponent(campaignName ?? '')}`)}
          className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark"
        >
          View campaign
        </button>
      </div>
    );
  }

  const blockReason = launchBlockReason();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/push-campaigns')}
            className="mb-2 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to campaigns
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">
            {campaign == null ? 'New push campaign' : form.campaign_name || 'Push campaign'}
          </h1>
        </div>
        <button
          onClick={handleSaveDraft}
          disabled={isSaving}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
        >
          <Save className="mr-2 h-4 w-4" />
          Save draft and exit
        </button>
      </div>

      <WizardSteps currentStep={step} onStepClick={goToStep} />

      {saveError != null && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{saveError}</p>
        </div>
      )}

      <div className="rounded-lg bg-white p-6 shadow">
        {step === 1 && (
          <AudienceStep
            campaignName={form.campaign_name}
            audienceType={form.audience_type}
            filters={form.filters}
            testEmails={form.test_emails}
            savedCampaignName={campaign?.name ?? null}
            errors={errors}
            onCampaignNameChange={x => updateForm({ campaign_name: x })}
            onAudienceTypeChange={x => updateForm({ audience_type: x })}
            onFiltersChange={x => updateForm({ filters: x })}
            onTestEmailsChange={x => updateForm({ test_emails: x })}
            onEstimateChange={setEstimate}
          />
        )}

        {step === 2 && (
          <ContentStep
            titleEn={form.title_en}
            bodyEn={form.body_en}
            titleFr={form.title_fr}
            bodyFr={form.body_fr}
            pictureLink={form.picture_link}
            errors={errors}
            onTitleEnChange={x => updateForm({ title_en: x })}
            onBodyEnChange={x => updateForm({ body_en: x })}
            onTitleFrChange={x => updateForm({ title_fr: x })}
            onBodyFrChange={x => updateForm({ body_fr: x })}
            onPictureLinkChange={x => updateForm({ picture_link: x })}
          />
        )}

        {step === 3 && (
          <div className="space-y-6">
            <ScheduleStep
              sendMode={form.send_mode}
              scheduledAt={form.scheduled_at}
              errors={errors}
              onSendModeChange={x => updateForm({ send_mode: x })}
              onScheduledAtChange={x => updateForm({ scheduled_at: x })}
            />
            {form.audience_type !== 'test_list' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Test addresses</label>
                <textarea
                  value={form.test_emails}
                  onChange={x => updateForm({ test_emails: x.target.value })}
                  rows={2}
                  placeholder="address@example.com, another@example.com"
                  className="mt-1 block w-full max-w-lg rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The mandatory test push goes to these existing app users, saved with the campaign.
                </p>
              </div>
            )}
            <TestSendPanel
              campaignName={campaign?.name ?? null}
              modified={campaign?.modified ?? null}
              canTestSend={canTestSend}
              isContentTested={isContentTested}
              onTestSent={handleTestSent}
              onBeforeSend={async () => {
                const saved = await save();
                if (saved == null) {
                  return null;
                }
                return { name: saved.name, modified: saved.modified };
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => goToStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </button>

        {step < LAST_STEP ? (
          <button
            onClick={handleNext}
            disabled={isSaving}
            className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Next'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        ) : (
          canLaunch && (
            <div className="flex items-center gap-3">
              {blockReason != null && <span className="text-sm text-gray-500">{blockReason}</span>}
              <button
                onClick={() => setIsConfirmOpen(true)}
                disabled={blockReason != null}
                className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                Review and send
              </button>
            </div>
          )
        )}
      </div>

      {step === LAST_STEP && isDirty && (
        <p className="text-sm text-gray-500">
          There are unsaved changes on this step. Use Save draft and exit to store them.
        </p>
      )}

      {campaign != null && (
        <ConfirmSendDialog
          open={isConfirmOpen}
          campaignName={campaign.name}
          modified={campaign.modified}
          audienceType={form.audience_type}
          filters={form.filters}
          sendMode={form.send_mode}
          scheduledAt={form.send_mode === 'scheduled' ? toBackendDateTime(form.scheduled_at) : null}
          onLaunched={handleLaunched}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}

      <ConfirmDialog
        open={isConflictOpen}
        title="The campaign changed elsewhere"
        message="Somebody else saved this campaign, or its status changed. Reload the data to continue, the current unsaved changes will be lost."
        confirmLabel="Reload data"
        cancelLabel="Keep editing"
        variant="warning"
        onConfirm={() => { setIsConflictOpen(false); reload(); }}
        onCancel={() => setIsConflictOpen(false)}
      />
    </div>
  );
};

function clampStep(value: string | null): number {
  const parsed = Number(value ?? '1');
  if (!Number.isFinite(parsed)) {
    return 1;
  }
  return Math.min(LAST_STEP, Math.max(1, Math.trunc(parsed)));
}

function toBackendDateTime(value: string): string | null {
  if (value === '') {
    return null;
  }
  return `${value.replace('T', ' ')}:00`.slice(0, 19);
}

function toInputDateTime(value: string | null): string {
  if (value == null || value === '') {
    return '';
  }
  return value.replace(' ', 'T').slice(0, 16);
}

function toForm(campaign: PushCampaign): PushCampaignForm {
  return {
    campaign_name: campaign.campaign_name ?? '',
    audience_type: campaign.audience_type,
    filters: readPushAudienceFilters(campaign.audience_json),
    test_emails: formatTestEmails(campaign.test_emails ?? []),
    title_en: campaign.title_en ?? '',
    body_en: campaign.body_en ?? '',
    title_fr: campaign.title_fr ?? '',
    body_fr: campaign.body_fr ?? '',
    picture_link: campaign.picture_link ?? '',
    send_mode: campaign.send_mode ?? 'now',
    scheduled_at: toInputDateTime(campaign.scheduled_at),
  };
}

function toPayload(form: PushCampaignForm): PushCampaignPayload {
  return {
    campaign_name: form.campaign_name.trim(),
    audience_type: form.audience_type,
    audience_json: toPushAudienceJson(form.audience_type, form.filters),
    test_emails: parseTestEmails(form.test_emails),
    title_en: form.title_en,
    title_fr: form.title_fr,
    body_en: form.body_en,
    body_fr: form.body_fr,
    picture_link: form.picture_link.trim(),
    send_mode: form.send_mode,
    scheduled_at: form.send_mode === 'scheduled' ? toBackendDateTime(form.scheduled_at) : null,
  };
}

